/**
 * EmptyBody — what fills the morph palette before the user has typed
 * or chatted. Renders SUGGESTED chips + sectioned rows + a footer
 * keyboard legend. Stagger-reveal animation handled by the parent's CSS
 * via `as-reveal` class on each child.
 */

import { MessageSquare } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '../lib/cn'
import { Sparkle } from './sparkle'

export type AgentSearchTint = 'blue' | 'green' | 'amber' | 'violet' | 'pink' | 'gray'

export interface AgentSearchItem {
  id: string
  title: string
  icon?: ReactNode
  meta?: string
  shortcut?: string[]
  tint?: AgentSearchTint
  href?: string
  /** '_blank' opens in a new tab on click. Defaults to same-tab nav. */
  target?: '_self' | '_blank'
  onSelect?: () => void
  /** When true, clicking the row runs `onSelect` but keeps the morph
   *  open. Used by RECENT rows that switch the morph into chat mode
   *  for an existing conversation — closing would just hide what the
   *  user clicked to see. */
  keepOpenOnSelect?: boolean
  /** Renders the row with strikethrough title + muted color (for
   *  done tasks). The icon swap to a filled check is the renderer's
   *  job upstream — see `KindIcon`. */
  completed?: boolean
}

export interface AgentSearchSection {
  id: string
  label: string
  items: AgentSearchItem[]
  /** Render a thin "── In other apps ──" divider above this section.
   *  Used by the search runner to separate host-app hits from cross-
   *  app federation hits. */
  dividerBefore?: boolean
}

export interface AgentSearchSuggestion {
  id: string
  label: string
  onSelect?: () => void
}

export interface EmptyBodyProps {
  suggestions: AgentSearchSuggestion[]
  sections: AgentSearchSection[]
  activeIdx: number
  /** Mouse-enter on a row syncs the active index so the keyboard
   *  highlight follows the cursor. */
  onRowEnter: (idx: number) => void
  onRowSelect: (item: AgentSearchItem) => void
  onSuggestionSelect: (suggestion: AgentSearchSuggestion) => void
  query: string
  onAskAgent: (query: string) => void
  /** When the user toggled "back to search" while a chat was active,
   *  show a resume row at the top of the empty body. Click → flip
   *  paletteOverride off and return to chat. */
  resumeChat?: {
    lastMessagePreview: string
    /** Role of the last message — used to label the preview "You: …" or
     *  "Agent: …" so the user knows whose turn the snippet is from. */
    lastMessageRole: 'user' | 'assistant'
    onResume: () => void
  }
  /** Renders the as-row-active accent on the resume row. Set true
   *  when the parent's `activeIdx` is on the synthetic resume item
   *  in `flatItems`, so keyboard nav + visual highlight stay in sync. */
  isResumeActive?: boolean
  /** Number of cross-app federation targets that timed out or
   *  errored. Surfaces as a single muted footer line. 0 hides it. */
  failedCount?: number
  /** True while a non-empty live search is debouncing/in flight and no
   *  results have landed yet. When true, renders a "Searching…" section
   *  with skeleton rows so users don't read in-flight as "no results".
   *  Per ticket d04cf091. */
  isSearching?: boolean
}

export function EmptyBody({
  suggestions,
  sections,
  activeIdx,
  onRowEnter,
  onRowSelect,
  onSuggestionSelect,
  query,
  onAskAgent,
  resumeChat,
  isResumeActive,
  failedCount = 0,
  isSearching = false,
}: EmptyBodyProps) {
  // Filter rows by query — case-insensitive substring match on title +
  // section label. Sections with zero matches drop entirely.
  //
  // Skip filtering for live-search sections (id starts with `live-`):
  // their hits were already server-filtered against the query and may
  // match on fields that aren't the title (e.g. RFP-Gremlin's deals
  // match by rfpNumber). Re-running an in-memory title substring would
  // drop legitimate hits.
  const q = query.trim().toLowerCase()
  const filtered = q
    ? sections
        .map((s) =>
          s.id.startsWith('live-')
            ? s
            : {
                ...s,
                items: s.items.filter((i) => i.title.toLowerCase().includes(q) || s.label.toLowerCase().includes(q)),
              },
        )
        .filter((s) => s.items.length > 0)
    : sections

  const showAskCta = q.length > 0

  // When the Resume row is rendered, it consumes flatItems[0] in the
  // parent's keyboard nav. Start section row indices at 1 so they
  // line up with flatItems[1..n], not flatItems[0..n-1] — otherwise
  // Enter on initial open lands on the first JUMP TO row instead of
  // Resume.
  const showsResume = resumeChat && !showAskCta
  let runningIdx = showsResume ? 1 : 0

  const totalRows = filtered.reduce((acc, s) => acc + s.items.length, 0)
  const showEmpty = !showAskCta && suggestions.length === 0 && totalRows === 0

  return (
    <>
      {resumeChat && !showAskCta ? (
        <button
          type="button"
          className={cn('as-reveal as-resume-row', isResumeActive && 'as-row-active')}
          onClick={(e) => {
            e.stopPropagation()
            resumeChat.onResume()
          }}
          aria-label="Resume your active chat">
          <span className="as-resume-icon">
            <MessageSquare />
          </span>
          <span className="as-resume-text">
            <span className="as-resume-title">Resume chat</span>
            {resumeChat.lastMessagePreview ? (
              <span className="as-resume-preview">
                <span className="as-resume-prefix">{resumeChat.lastMessageRole === 'user' ? 'You:' : 'Agent:'}</span>{' '}
                {resumeChat.lastMessagePreview}
              </span>
            ) : null}
          </span>
          <span className="as-kbd as-kbd-sm">↵</span>
        </button>
      ) : null}

      {showAskCta ? (
        <div className="as-reveal as-ask-cta" onClick={() => onAskAgent(query)} role="option" aria-selected={false}>
          <Sparkle size={14} />
          <span className="as-ask-cta-label">Ask agent:</span>
          <span className="as-ask-cta-query">{query}</span>
        </div>
      ) : null}

      {!showAskCta && suggestions.length > 0 ? (
        <>
          <div className="as-reveal as-section-label">Suggested</div>
          <div className="as-reveal as-prompts">
            {suggestions.map((s) => (
              <button key={s.id} type="button" className="as-chip" onClick={() => onSuggestionSelect(s)}>
                <Sparkle size={13} />
                {s.label}
              </button>
            ))}
          </div>
        </>
      ) : null}

      {filtered.map((section) => (
        <div key={section.id}>
          {section.dividerBefore ? (
            <div className="as-reveal as-divider" aria-hidden>
              <span className="as-divider-line" />
              <span className="as-divider-label">In other apps</span>
              <span className="as-divider-line" />
            </div>
          ) : null}
          <div className="as-reveal as-section-label">{section.label}</div>
          {section.items.map((item) => {
            const idx = runningIdx++
            const isActive = idx === activeIdx
            return (
              <div
                key={item.id}
                className={cn('as-reveal as-row', isActive && 'as-row-active', item.completed && 'as-row-completed')}
                onClick={() => onRowSelect(item)}
                onMouseEnter={() => onRowEnter(idx)}
                role="option"
                aria-selected={isActive}>
                <div className={cn('as-row-icon', item.tint && `as-tint-${item.tint}`)}>{item.icon}</div>
                <div className="as-row-title">{item.title}</div>
                {item.meta ? <div className="as-row-meta">{item.meta}</div> : null}
                {item.shortcut ? (
                  <div className="as-row-shortcut">
                    {item.shortcut.map((k, i) => (
                      <span key={i} className="as-kbd as-kbd-sm">
                        {k}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      ))}

      {/* Skeleton rows while a live search is in flight — keeps users
       *  from reading the silent gap as "no results" and dropping
       *  straight into AI chat. Renders 3 placeholder rows that match
       *  the .as-row layout. Only shown when no live results have
       *  landed yet (totalRows === 0). */}
      {isSearching && totalRows === 0 ? (
        <div aria-live="polite" aria-busy="true">
          <div className="as-reveal as-section-label">Searching…</div>
          {[0, 1, 2].map((i) => (
            <div key={`skel-${i}`} className="as-reveal as-row" aria-hidden>
              <span className="as-row-icon">
                <span className="as-skeleton-dot" />
              </span>
              <span className="as-skeleton-line as-skeleton-line-md" />
            </div>
          ))}
        </div>
      ) : null}

      {failedCount > 0 ? (
        <div className="as-reveal as-failed-line" aria-live="polite">
          ({failedCount} {failedCount === 1 ? 'app' : 'apps'} couldn&apos;t be searched)
        </div>
      ) : null}

      {showEmpty && !isSearching ? <div className="as-reveal as-empty">Start typing to ask the agent…</div> : null}
    </>
  )
}
